<template>
  <div class="chart-wrapper" :class="wrapperClass">
    <div v-if="title" class="chart-header">
      <h4 class="chart-title">{{ title }}</h4>
      <p v-if="subtitle" class="chart-subtitle">{{ subtitle }}</p>
    </div>
    
    <div v-if="isLoading" class="chart-loading">
      <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
      <p>{{ loadingText }}</p>
    </div>
    
    <div v-else-if="hasError" class="chart-error">
      <i class="pi pi-exclamation-triangle" style="font-size: 2rem;"></i>
      <p>{{ errorMessage }}</p>
    </div>
    
    <div v-else-if="!hasData" class="chart-no-data">
      <i class="pi pi-info-circle" style="font-size: 2rem;"></i>
      <p>{{ noDataText }}</p>
    </div>
    
    <div 
      v-else 
      :id="chartId" 
      class="chart-container"
      :style="{ height: height, width: width }">
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import ApexCharts from 'apexcharts';
import ProgressSpinner from 'primevue/progressspinner';
import { log } from '../utils/DebugLogger';

type ChartType = 'line' | 'bar' | 'donut' | 'treemap' | 'area' | 'pie' | 'radialBar';
type ChartTheme = 'light' | 'dark';

const props = withDefaults(defineProps<{
  // Chart type: 'line', 'bar', 'donut', 'treemap'
  type: ChartType;
  // Chart data series
  series: any[];
  // Chart options (will be merged with defaults)
  options?: Record<string, any>;
  // Chart dimensions
  height?: string;
  width?: string;
  // Title and subtitle
  title?: string;
  subtitle?: string;
  // Loading state
  isLoading?: boolean;
  loadingText?: string;
  // Error state
  hasError?: boolean;
  errorMessage?: string;
  // No data state
  noDataText?: string;
  // Additional wrapper classes
  wrapperClass?: string;
  // Animation enabled
  animate?: boolean;
  // Theme: 'light' or 'dark'
  theme?: ChartTheme;
}>(), {
  options: () => ({}),
  height: '350px',
  width: '100%',
  title: '',
  subtitle: '',
  isLoading: false,
  loadingText: 'Carregando dados...',
  hasError: false,
  errorMessage: 'Erro ao carregar dados do gráfico',
  noDataText: 'Nenhum dado disponível',
  wrapperClass: '',
  animate: true,
  theme: 'light'
});

const emit = defineEmits<{
  'chart-rendered': [instance: any];
  'chart-updated': [instance: any];
  'chart-destroyed': [];
}>();

// Get i18n for number formatting
const { n } = useI18n();

// Generate unique chart ID
const chartId = ref<string>(`chart-${Math.random().toString(36).substr(2, 9)}`);
const chartInstance = ref<any>(null);
const isInitializing = ref<boolean>(false);

// Check if chart has data
const hasData = computed(() => {
  if (!props.series || props.series.length === 0) return false;

  // For donut/pie charts, check if data array has values
  if (['donut', 'pie', 'radialBar'].includes(props.type)) {
    return props.series.some((val: any) => val > 0);
  }

  // For other charts, check if series has data
  return props.series.some((s: any) => s.data && s.data.length > 0);
});

// Get default options based on chart type
const getDefaultOptions = (): Record<string, any> => {
  const baseOptions = {
    chart: {
      type: props.type,
      height: props.height,
      width: props.width,
      animations: {
        enabled: props.animate,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false
        }
      },
      fontFamily: 'Helvetica, Arial, sans-serif'
    },
    theme: {
      mode: props.theme,
      palette: 'palette1'
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth',
      width: 2
    },
    grid: {
      borderColor: props.theme === 'dark' ? '#444' : '#e7e7e7',
      strokeDashArray: 4,
      position: 'back'
    },
    tooltip: {
      enabled: true,
      theme: props.theme,
      x: {
        show: true
      },
      y: {
        formatter: (val: any) => {
          if (typeof val === 'number') {
            return n(val, 'decimal');
          }
          return val;
        }
      }
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '14px',
      fontWeight: 400,
      labels: {
        colors: props.theme === 'dark' ? '#fff' : '#333'
      }
    },
    noData: {
      text: props.noDataText,
      align: 'center',
      verticalAlign: 'middle',
      style: {
        color: '#999',
        fontSize: '14px'
      }
    }
  };

  // Type-specific options
  switch (props.type) {
    case 'line':
    case 'area':
      return {
        ...baseOptions,
        stroke: {
          curve: 'smooth',
          width: props.type === 'line' ? 3 : 2
        },
        markers: {
          size: props.type === 'line' ? 4 : 0,
          strokeWidth: 2,
          hover: {
            size: 6
          }
        },
        fill: {
          type: props.type === 'area' ? 'gradient' : 'solid',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.3,
            stops: [0, 90, 100]
          }
        }
      };

    case 'bar':
      return {
        ...baseOptions,
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '55%',
            borderRadius: 4,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        }
      };

    case 'donut':
    case 'pie':
      return {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: props.type
        },
        plotOptions: {
          pie: {
            donut: {
              size: props.type === 'donut' ? '65%' : '0%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '16px',
                  fontWeight: 600
                },
                value: {
                  show: true,
                  fontSize: '24px',
                  fontWeight: 700,
                  formatter: (val: any) => {
                    if (typeof val === 'number') {
                      return n(val, 'decimal');
                    }
                    return val;
                  }
                },
                total: {
                  show: true,
                  label: 'Total',
                  fontSize: '16px',
                  fontWeight: 600,
                  formatter: (w: any) => {
                    const total = w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0);
                    return n(total, 'decimal');
                  }
                }
              }
            }
          }
        },
        dataLabels: {
          enabled: true,
          formatter: (val: number) => {
            return val.toFixed(1) + '%';
          }
        },
        stroke: {
          width: 2,
          colors: [props.theme === 'dark' ? '#1f1f1f' : '#fff']
        }
      };

    case 'treemap':
      return {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: 'treemap'
        },
        plotOptions: {
          treemap: {
            distributed: true,
            enableShades: false
          }
        },
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '12px'
          },
          formatter: (text: string, op: any) => {
            return [text, op.value];
          }
        }
      };

    case 'radialBar':
      return {
        ...baseOptions,
        chart: {
          ...baseOptions.chart,
          type: 'radialBar'
        },
        plotOptions: {
          radialBar: {
            startAngle: -135,
            endAngle: 135,
            hollow: {
              margin: 0,
              size: '70%',
              background: props.theme === 'dark' ? '#1f1f1f' : '#fff'
            },
            track: {
              background: props.theme === 'dark' ? '#444' : '#e7e7e7',
              strokeWidth: '67%',
              margin: 0
            },
            dataLabels: {
              show: true,
              name: {
                offsetY: -10,
                show: true,
                fontSize: '16px'
              },
              value: {
                formatter: (val: number) => {
                  return val.toFixed(1) + '%';
                },
                fontSize: '30px',
                show: true
              }
            }
          }
        },
        fill: {
          type: 'gradient',
          gradient: {
            shade: 'dark',
            type: 'horizontal',
            shadeIntensity: 0.5,
            gradientToColors: ['#ABE5A1'],
            inverseColors: true,
            opacityFrom: 1,
            opacityTo: 1,
            stops: [0, 100]
          }
        },
        stroke: {
          lineCap: 'round'
        }
      };

    default:
      return baseOptions;
  }
};

// Merge user options with defaults
const mergedOptions = computed(() => {
  const defaults = getDefaultOptions();
  return deepMerge(defaults, props.options);
});

// Deep merge utility
const deepMerge = (target: Record<string, any>, source: Record<string, any>): Record<string, any> => {
  const output = { ...target };
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
};

const isObject = (item: any): boolean => {
  return item && typeof item === 'object' && !Array.isArray(item);
};

// Initialize chart
const initChart = async (): Promise<void> => {
  // Prevent multiple simultaneous initializations
  if (isInitializing.value) {
    log('Charts', 'initChart', 'Already initializing, skipping');
    return;
  }

  log('Charts', 'initChart', `Initializing ${props.type} chart`, { id: chartId.value });

  if (!hasData.value) {
    log('Charts', 'initChart', 'No data available, skipping initialization');
    return;
  }

  isInitializing.value = true;

  try {
    await nextTick();

    const element = document.getElementById(chartId.value);
    if (!element) {
      log('Charts', 'initChart', 'Chart element not found', chartId.value);
      return;
    }

    // Destroy existing chart if any
    if (chartInstance.value) {
      await chartInstance.value.destroy();
      chartInstance.value = null;
    }

    const chartConfig = {
      series: props.series,
      ...mergedOptions.value
    };

    log('Charts', 'initChart', 'Creating chart with config', chartConfig);

    chartInstance.value = new ApexCharts(element, chartConfig);
    await chartInstance.value.render();

    log('Charts', 'initChart', 'Chart rendered successfully');
    emit('chart-rendered', chartInstance.value);
  } catch (error) {
    console.error('Error initializing chart:', error);
    log('Charts', 'initChart', 'Error', error);
  } finally {
    isInitializing.value = false;
  }
};

// Update chart
const updateChart = async (): Promise<void> => {
  // Don't update if currently initializing
  if (isInitializing.value) {
    log('Charts', 'updateChart', 'Currently initializing, skipping update');
    return;
  }

  if (!chartInstance.value) {
    log('Charts', 'updateChart', 'No chart instance, reinitializing');
    await initChart();
    return;
  }

  if (!hasData.value) {
    log('Charts', 'updateChart', 'No data available, destroying chart');
    await chartInstance.value.destroy();
    chartInstance.value = null;
    return;
  }

  try {
    log('Charts', 'updateChart', 'Updating chart with new data');

    // Verify element still exists before updating
    const element = document.getElementById(chartId.value);
    if (!element) {
      log('Charts', 'updateChart', 'Chart element not found, will reinitialize on next mount');
      chartInstance.value = null;
      return;
    }

    await chartInstance.value.updateOptions(mergedOptions.value);
    await chartInstance.value.updateSeries(props.series);
    emit('chart-updated', chartInstance.value);
  } catch (error) {
    console.error('Error updating chart:', error);
    log('Charts', 'updateChart', 'Error updating, will reinitialize', error);

    // Only reinitialize if not already initializing and element exists
    const element = document.getElementById(chartId.value);
    if (element && !isInitializing.value) {
      chartInstance.value = null;
      await initChart();
    }
  }
};

// Destroy chart
const destroyChart = async (): Promise<void> => {
  if (chartInstance.value) {
    log('Charts', 'destroyChart', 'Destroying chart');
    await chartInstance.value.destroy();
    chartInstance.value = null;
    emit('chart-destroyed');
  }
};

// Watch for data changes
watch(() => props.series, async () => {
  log('Charts', 'watch:series', 'Series data changed');
  await updateChart();
}, { deep: true });

watch(() => props.options, async () => {
  log('Charts', 'watch:options', 'Options changed');
  await updateChart();
}, { deep: true });

watch(() => props.type, async () => {
  log('Charts', 'watch:type', 'Chart type changed');
  await initChart();
});

watch(() => props.isLoading, async (newVal: boolean, oldVal: boolean) => {
  log('Charts', 'watch:isLoading', `Loading state changed: ${oldVal} -> ${newVal}`);
  if (!newVal && hasData.value) {
    await nextTick();
    await initChart();
  }
});

// Lifecycle hooks
onMounted(async () => {
  log('Charts', 'onMounted', 'Component mounted');
  if (!props.isLoading && hasData.value) {
    await initChart();
  }
});

onBeforeUnmount(async () => {
  log('Charts', 'onBeforeUnmount', 'Component unmounting');
  await destroyChart();
});

// Expose methods for parent components
defineExpose({
  refresh: initChart,
  update: updateChart,
  destroy: destroyChart,
  getInstance: () => chartInstance.value
});
</script>

<style scoped>
.chart-wrapper {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.chart-header {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e7e7e7;
}

.chart-title {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.chart-subtitle {
  margin: 0;
  font-size: 14px;
  color: #666;
}

.chart-container {
  width: 100%;
}

.chart-loading,
.chart-error,
.chart-no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: #999;
}

.chart-loading i,
.chart-error i,
.chart-no-data i {
  margin-bottom: 15px;
  color: #ccc;
}

.chart-loading p,
.chart-error p,
.chart-no-data p {
  margin: 0;
  font-size: 14px;
  color: #999;
}

.chart-error {
  color: #d9534f;
}

.chart-error i {
  color: #d9534f;
}

/* Dark theme support */
.chart-wrapper.dark {
  background: #1f1f1f;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.chart-wrapper.dark .chart-header {
  border-bottom-color: #444;
}

.chart-wrapper.dark .chart-title {
  color: #fff;
}

.chart-wrapper.dark .chart-subtitle {
  color: #ccc;
}

/* Responsive */
@media (max-width: 768px) {
  .chart-wrapper {
    padding: 15px;
  }
  
  .chart-title {
    font-size: 16px;
  }
  
  .chart-subtitle {
    font-size: 12px;
  }
}
</style>

